import type { GameTab } from '../../engine/tabs';
import { showToast } from '../../engine/toast';
import { isReduceMotionEnabled } from '../../engine/reduce-motion';
import { fetchColonyPowerRank, fetchProfile, fetchTitles } from './profile-api';
import type { PersonalRank, ProfileView, TitleRarity, TitleView } from './profile-api';
import { openTitlePicker, RARITY_TINT, type TitlePickerHandle } from './title-picker';
import { SCENE_FOR_RARITY } from './title-scene-map';
import { fetchCurrentSeason } from './season-api';
import type { SeasonCurrentView } from './season-api';
import { fetchAllColonies, fetchTeamColorPalette, updateTeamColors } from '../colony/colony-api';
import type { ColonyView, TeamColor } from '../colony/colony-api';
import { t, tOr } from '../../engine/i18n/i18n';

/** How often the season counter re-reads the clock. A season spans months, so this only needs to be "eventually accurate", not per-second. */
const SEASON_TICK_MS = 60_000;

/** Poster + loop for the ambient hab-viewport backdrop. The poster alone renders the screen completely; the video is pure enhancement. */
const AMBIENT_POSTER = '/profile/hab-viewport-1k.webp';
const AMBIENT_POSTER_2K = '/profile/hab-viewport-2k.webp';
const AMBIENT_VIDEO_WEBM = '/profile/hab-viewport.webm';
const AMBIENT_VIDEO_MP4 = '/profile/hab-viewport.mp4';
/** Above this CSS width the 2k poster is worth its bytes — mirrors engine/colony-background.ts's SIZE_SWITCH_WIDTH_PX. */
const POSTER_SWITCH_WIDTH_PX = 1024;

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

/** "23d 4h" / "4h 12m" / "12m" — a season's reset is always at least minutes away by the time anyone reads this, so there's no seconds tier. */
export function formatSeasonCountdown(remainingMs: number): string {
  const totalMinutes = Math.max(0, Math.floor(remainingMs / 60_000));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  return `${minutes}m`;
}

/**
 * The same remaining-time split as `formatSeasonCountdown`, but kept as
 * separate numbers so the counter can render each unit into its own recessed
 * digit cell instead of one text run. Zero-padded to two characters because
 * the cells are fixed-width — a jumping single digit reads as a glitch.
 */
export function seasonCountdownParts(remainingMs: number): { days: string; hours: string; minutes: string } {
  const totalMinutes = Math.max(0, Math.floor(remainingMs / 60_000));
  return {
    days: String(Math.floor(totalMinutes / 1440)).padStart(2, '0'),
    hours: String(Math.floor((totalMinutes % 1440) / 60)).padStart(2, '0'),
    minutes: String(totalMinutes % 60).padStart(2, '0'),
  };
}

/**
 * Fraction of the season already elapsed, clamped to [0, 1]. Drives the
 * counter's progress rail. Guards a zero-length span (a misconfigured season
 * would otherwise divide by zero and render NaN%).
 */
export function seasonElapsedFraction(startsAt: string, endsAt: string, now: number): number {
  const start = new Date(startsAt).getTime();
  const end = new Date(endsAt).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
  return Math.min(1, Math.max(0, (now - start) / (end - start)));
}

/** "2 Oct 2026" — short, unambiguous, and locale-independent in its ordering. */
export function formatJoinDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Thousands-separated, so a six-figure Colony Power stays readable at a glance. */
export function formatPoints(value: number): string {
  return value.toLocaleString('en-GB');
}

export function renderProfileScreen(root: HTMLElement): () => void {
  // Three nested elements, each earning its place:
  //
  //  1. A NON-scrolling `relative` wrapper sized to the shell's content area.
  //  2. The ambient backdrop, `absolute inset-0` inside it — so it is pinned
  //     to that area and does not move. It must NOT live inside the scroll
  //     container: an absolutely-positioned layer there anchors to the
  //     scrolled content and slides off-screen on a tall mobile profile.
  //  3. The scrolling content, layered above it.
  //
  // `z-index` runs 0/10 with nothing negative. This screen mounts into the
  // shell's `#shell-content`, whose nearest positioned ancestor paints an
  // opaque --color-bg — a `z-index:-1` backdrop would paint behind that and
  // be invisible.
  //
  // The backdrop is also a SIBLING of the re-rendered content, never a child:
  // `render()` replaces `container.innerHTML` wholesale, which would
  // otherwise tear down and restart the <video> from frame 0.
  root.innerHTML = `
    <div class="relative w-full h-full overflow-hidden">
      <div data-ambient aria-hidden="true" class="absolute inset-0 pointer-events-none z-0"></div>
      <div id="profile-screen" class="relative z-10 w-full h-full overflow-y-auto overscroll-contain">
        <div class="text-center text-text-dim py-10">${t('profile.loading')}</div>
      </div>
    </div>
  `;
  const container = root.querySelector('#profile-screen') as HTMLDivElement;
  const ambient = root.querySelector('[data-ambient]') as HTMLDivElement;

  let disposed = false;
  let profile: ProfileView | null = null;
  let colonies: ColonyView[] = [];
  let palette: TeamColor[] = [];
  let season: SeasonCurrentView | null = null;
  let titles: TitleView[] = [];
  let rank: PersonalRank | null = null;

  // ------------------------------------------------------- ambient backdrop

  /**
   * Poster always; video only when motion is allowed.
   *
   * CSS cannot pause a <video>, so the existing reduce-motion machinery
   * (app.css's [data-reduce-motion] rule, which only kills animation and
   * transition) would silently fail to stop it — the Settings toggle would
   * become a lie. Both the explicit toggle and the OS-level preference are
   * therefore checked here, in JS, and the video is simply never created.
   * Switching tabs remounts this screen, so a changed toggle is picked up
   * without needing a subscription.
   */
  function mountAmbient(): void {
    const poster = window.innerWidth >= POSTER_SWITCH_WIDTH_PX ? AMBIENT_POSTER_2K : AMBIENT_POSTER;
    const stillOnly =
      isReduceMotionEnabled() || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

    ambient.innerHTML = `
      <div class="absolute inset-0 bg-cover bg-center" style="background-image:url('${poster}')"></div>
      ${
        stillOnly
          ? ''
          : `<video data-ambient-video autoplay muted loop playsinline preload="metadata" poster="${poster}"
               class="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-700">
               <source src="${AMBIENT_VIDEO_WEBM}" type="video/webm">
               <source src="${AMBIENT_VIDEO_MP4}" type="video/mp4">
             </video>`
      }
      <div class="absolute inset-0 ambient-scrim"></div>
    `;

    // Fade in only once there are real frames, so a missing or slow asset
    // leaves the poster showing rather than flashing a black rectangle.
    const video = ambient.querySelector('[data-ambient-video]') as HTMLVideoElement | null;
    video?.addEventListener('canplay', () => video.classList.remove('opacity-0'), { once: true });
  }

  // ------------------------------------------------------------ player card

  function titlePlateHtml(): string {
    const label = profile?.titleLabel;
    if (!label) {
      return `<div class="text-[10px] text-text-dim uppercase tracking-[0.2em]">${t('profile.noTitle')}</div>`;
    }
    const rarity = (profile?.titleRarity ?? 'MISSION') as TitleRarity;
    const tint = RARITY_TINT[rarity];
    // `pt-plate` + data-scene: the plate is made of the same material as that group's equip scene.
    return `<div class="title-plate pt-plate" style="--title-tint:${tint}" data-rarity="${rarity}" data-scene="${SCENE_FOR_RARITY[rarity]}" data-profile-title><span>${escapeHtml(label)}</span></div>`;
  }

  function playerCardHtml(): string {
    if (!profile) {
      return `<div class="card-plate p-5"><p class="text-xs text-text-dim">${t('profile.accountLoadFailed')}</p></div>`;
    }
    return `
      <div class="card-plate card-sheen p-5 pt-6 flex flex-col gap-3">
        <div class="flex flex-col items-start gap-2">
          ${titlePlateHtml()}
          <h2 class="font-heading text-xl sm:text-2xl text-text leading-tight break-words [text-shadow:0_2px_8px_rgba(0,0,0,0.7)]"
            data-profile-username>${escapeHtml(profile.username ?? '—')}</h2>
          <div class="text-[11px] text-text-dim truncate max-w-full" data-profile-email>${escapeHtml(profile.email)}</div>
        </div>

        <hr class="etched-rule my-1">

        <dl class="grid grid-cols-2 gap-3">
          <div>
            <dt class="text-[9px] text-text-dim uppercase tracking-[0.16em]">${t('profile.enlisted')}</dt>
            <dd class="text-sm text-text tabular-nums" data-profile-joined>${formatJoinDate(profile.createdAt)}</dd>
          </div>
          <div>
            <dt class="text-[9px] text-text-dim uppercase tracking-[0.16em]">${t('profile.colonyPower')}</dt>
            <dd class="text-sm text-gold-bright font-bold tabular-nums" data-profile-power>${formatPoints(profile.colonyPower)}</dd>
          </div>
        </dl>

        <button type="button" data-action="open-titles"
          class="font-button min-h-11 px-4 mt-1 bg-gold text-bg text-xs rounded transition-colors duration-[120ms] hover:bg-gold-bright touch-manipulation">
          ${t('profile.changeTitle')}
        </button>
      </div>
    `;
  }

  // --------------------------------------------------------- season counter

  /**
   * Segmented countdown over the shared `.mars-horizon` backdrop, so the
   * profile and colony screens keep one visual language for "this is Mars".
   * Degrades to nothing (not an error state) when the season fetch failed —
   * the rest of the screen matters more than this panel.
   */
  function seasonPanelHtml(): string {
    if (!season) return '';
    const remaining = new Date(season.season.endsAt).getTime() - Date.now();
    const parts = seasonCountdownParts(remaining);
    const elapsed = seasonElapsedFraction(season.season.startsAt, season.season.endsAt, Date.now());

    const cell = (unit: 'days' | 'hours' | 'minutes', value: string, label: string) => `
      <div class="flex flex-col items-center gap-1.5">
        <div class="counter-well w-[4.25rem] h-16 sm:w-24 sm:h-20 text-3xl sm:text-5xl font-bold" data-countdown-unit="${unit}">${value}</div>
        <span class="text-xs sm:text-sm font-bold text-gold uppercase tracking-[0.2em] [text-shadow:0_0_10px_rgba(226,96,31,0.55)]">${label}</span>
      </div>
    `;

    // Seasons are named "Season 3" until someone gives them a real name, so
    // the eyebrow and the heading would otherwise read "Mars Season 3" /
    // "Season 3". Show the name only when it actually adds something.
    const hasRealName = season.season.name.trim().toLowerCase() !== `season ${season.season.number}`;

    return `
      <section class="mars-horizon rounded-lg px-4 py-5 flex flex-col items-center gap-3">
        <span class="text-[10px] text-text-dim uppercase tracking-[0.22em]">${t('profile.season.eyebrow', { number: season.season.number })}</span>
        ${
          hasRealName
            ? `<h2 class="font-heading text-lg sm:text-xl text-gold-bright tracking-wide text-center [text-shadow:0_1px_10px_rgba(0,0,0,0.8)]"
                 data-season-name>${escapeHtml(season.season.name)}</h2>`
            : ''
        }

        <div class="holo-countdown flex items-start gap-2 sm:gap-7" role="timer" aria-label="${t('profile.season.timerLabel')}">
          ${cell('days', parts.days, t('profile.season.days'))}
          ${cell('hours', parts.hours, t('profile.season.hours'))}
          ${cell('minutes', parts.minutes, t('profile.season.minutes'))}
        </div>

        <!-- Screen-reader-friendly plain text, and the hook the minute tick patches. -->
        <div class="sr-only" data-season-countdown>${t('profile.season.resetsIn', { time: formatSeasonCountdown(remaining) })}</div>

        <div class="w-full max-w-xs">
          <div class="h-1 rounded-full bg-black/50 overflow-hidden">
            <div class="h-full bg-gradient-to-r from-gold to-gold-bright" data-season-progress
              style="width:${(elapsed * 100).toFixed(2)}%"></div>
          </div>
          <div class="flex justify-between text-[9px] text-text-dim mt-1">
            <span>${t('profile.season.points')}</span>
            <span class="text-text font-bold tabular-nums">${formatPoints(season.colonySeasonPoints)}</span>
          </div>
        </div>
      </section>
    `;
  }

  // ------------------------------------------------------------- rank block

  /**
   * Standing only — never the score. `colonyPower` IS the rank's underlying
   * value, so printing it here too would show the same number twice and read
   * as a bug; the card above owns the magnitude.
   */
  function rankHtml(): string {
    const body = rank
      ? `
        <div class="flex items-center gap-3">
          <div class="rank-emblem w-[4.75rem] h-[5.25rem] text-xl shrink-0" data-rank-position>#${rank.rank}</div>
          <div class="min-w-0">
            <div class="text-sm text-text">${t('profile.rankOf', { total: formatPoints(rank.total) })}</div>
            <div class="text-[10px] text-text-dim">${t('profile.rankedBy')}</div>
          </div>
        </div>`
      : `<p class="text-xs text-text-dim">${t('profile.standingUnavailable')}</p>`;

    return `
      <section class="glass-panel px-4 py-3 flex flex-col gap-2.5" data-rank-section>
        <h2 class="font-heading text-xs text-gold tracking-wide uppercase">${t('profile.standing')}</h2>
        ${body}
        <button type="button" data-action="open-rankings"
          class="min-h-11 px-3 rounded border border-border/60 text-text text-xs text-left transition-colors duration-[120ms] hover:border-gold hover:text-gold touch-manipulation">
          ${t('profile.fullRankings')}
        </button>
      </section>
    `;
  }

  // --------------------------------------------------------- colony portfolio

  function coloniesHtml(): string {
    const body =
      colonies.length === 0
        ? `<p class="text-xs text-text-dim">${t('profile.colonies.empty')}</p>`
        : colonies
            .map(
              (c) => `
        <li class="flex items-center justify-between gap-2 py-1.5 border-b border-border/40 last:border-0">
          <div class="min-w-0">
            <div class="text-sm text-text truncate" data-profile-colony-name>${escapeHtml(c.name)}</div>
            <div class="text-[10px] text-text-dim tabular-nums">${t('profile.colonies.seasonPts', { points: formatPoints(c.seasonPoints) })}</div>
          </div>
          <span class="tier-badge shrink-0" data-profile-colony-tier>${t('profile.colonies.tier', { tier: c.tier })}</span>
        </li>`,
            )
            .join('');

    return `
      <section class="glass-panel px-4 py-3 flex flex-col gap-2" data-colony-summary-section>
        <div class="flex items-baseline justify-between gap-2">
          <h2 class="font-heading text-xs text-gold tracking-wide uppercase">${t('term.colonies')}</h2>
          <span class="text-[10px] text-text-dim tabular-nums">${colonies.length}</span>
        </div>
        <ul class="flex flex-col">${body}</ul>
      </section>
    `;
  }

  // ------------------------------------------------------------ team colours

  /**
   * Team livery picker. Primary is what units are painted in; secondary only
   * appears when an opponent brought the same primary to a battle, so the two
   * sides stay tellable apart. Swatches are sized to the 44px minimum tap
   * target.
   *
   * TRANSITIONAL, same caveat as the backend's `getColonyForUser`: livery is
   * a per-COLONY property, but this edits only the founding (oldest) colony —
   * the one `PATCH /me/colony/team-colors` still resolves to.
   */
  // Team colours are switched off (see render()). Kept, and referenced via `void`, so it stays compiled and easy to restore.
  void (() => liveryHtml);
  function liveryHtml(): string {
    const colony = colonies[0];
    if (!colony || palette.length === 0) return '';
    const swatches = (slot: 'primary' | 'secondary', selected: string, disabled: string) =>
      palette
        .map((c) => {
          const isSel = c.id === selected;
          const isBlocked = c.id === disabled;
          const ring = isSel ? 'ring-2 ring-gold-bright' : 'ring-1 ring-border';
          return `<button type="button"
            data-action="pick-color" data-slot="${slot}" data-color-id="${c.id}"
            ${isBlocked ? 'disabled' : ''}
            aria-pressed="${isSel}"
            title="${isBlocked ? t('profile.livery.blocked', { label: tOr(`term.color.${c.id}`, c.label) }) : tOr(`term.color.${c.id}`, c.label)}"
            class="min-w-11 min-h-11 p-1.5 rounded ${ring} ${isBlocked ? 'opacity-25 cursor-not-allowed' : 'hover:ring-gold'} transition-shadow touch-manipulation flex items-center justify-center">
            <span class="w-full h-full rounded-sm" style="background:${c.hex}" aria-hidden="true"></span>
            <span class="sr-only">${tOr(`term.color.${c.id}`, c.label)}</span>
          </button>`;
        })
        .join('');
    return `
      <section class="glass-panel px-4 py-3 flex flex-col gap-2.5" data-livery-section>
        <h2 class="font-heading text-xs text-gold tracking-wide uppercase">${t('profile.livery.title')}</h2>
        <div>
          <div class="text-[10px] text-text-dim mb-1">${t('profile.livery.primary')}</div>
          <div class="flex flex-wrap gap-1.5">${swatches('primary', colony.primaryColor, colony.secondaryColor)}</div>
        </div>
        <div>
          <div class="text-[10px] text-text-dim mb-1">${t('profile.livery.secondary')} <span class="opacity-70">${t('profile.livery.secondaryHint')}</span></div>
          <div class="flex flex-wrap gap-1.5">${swatches('secondary', colony.secondaryColor, colony.primaryColor)}</div>
        </div>
      </section>
    `;
  }

  // ------------------------------------------------------------------ render

  /**
   * Asymmetric on desktop, single column on mobile — the card is a fixed-width
   * left rail that stays put while the right column scrolls, rather than one
   * centered stack. Below `lg` it collapses to one column, card first, which
   * is what the mobile-first mandate requires.
   */
  function render(): void {
    container.innerHTML = `
      <div class="max-w-5xl mx-auto p-4 sm:p-6 pb-12 grid gap-5 lg:grid-cols-[minmax(320px,380px)_1fr] lg:items-start">
        <div class="lg:sticky lg:top-6">${playerCardHtml()}</div>
        <div class="flex flex-col gap-4 min-w-0">
          ${seasonPanelHtml()}
          ${rankHtml()}
          ${coloniesHtml()}
          ${/* Team colours: switched off, not in use right now. Restore by re-adding `liveryHtml()` here
               (the picker, its API calls and the click handlers are all still in place). */ ''}
        </div>
      </div>
    `;
  }

  // -------------------------------------------------------------- countdown

  /**
   * Surgical countdown refresh — writes into the nodes the panel already
   * rendered instead of calling `render()`. Same reasoning as
   * colony-screen.ts's `patchResources()`: a full `container.innerHTML =`
   * every minute would throw away scroll position and any focus/aria state on
   * the livery swatches. It would also be invisible churn behind the ambient
   * layer rather than reaching it (the video is a sibling), but the scroll
   * and focus reasons stand on their own.
   */
  function patchSeasonCountdown(): void {
    if (!season) return;
    const remaining = new Date(season.season.endsAt).getTime() - Date.now();
    const label = container.querySelector('[data-season-countdown]');
    if (label) label.textContent = t('profile.season.resetsIn', { time: formatSeasonCountdown(remaining) });

    const parts = seasonCountdownParts(remaining);
    for (const [unit, value] of Object.entries(parts)) {
      const cell = container.querySelector(`[data-countdown-unit="${unit}"]`);
      if (cell) cell.textContent = value;
    }

    const rail = container.querySelector('[data-season-progress]') as HTMLElement | null;
    if (rail) {
      const fraction = seasonElapsedFraction(season.season.startsAt, season.season.endsAt, Date.now());
      rail.style.width = `${(fraction * 100).toFixed(2)}%`;
    }
  }

  // -------------------------------------------------------------------- data

  /**
   * Every source is independent and must degrade on its own — a colonyless
   * account (fetchAllColonies returning [], the palette or rank call
   * rejecting) must still show identity, not blank the whole screen.
   */
  async function load(): Promise<void> {
    const [profileResult, coloniesResult, paletteResult, seasonResult, titlesResult, rankResult] =
      await Promise.allSettled([
        fetchProfile(),
        fetchAllColonies(),
        fetchTeamColorPalette(),
        fetchCurrentSeason(),
        fetchTitles(),
        fetchColonyPowerRank(),
      ]);
    if (disposed) return;

    if (profileResult.status === 'fulfilled') {
      profile = profileResult.value;
    } else {
      const message =
        profileResult.reason instanceof Error ? profileResult.reason.message : t('profile.toast.accountLoadFailed');
      showToast(message, 'error');
    }
    if (coloniesResult.status === 'fulfilled') colonies = coloniesResult.value;
    if (paletteResult.status === 'fulfilled') palette = paletteResult.value;
    if (seasonResult.status === 'fulfilled') season = seasonResult.value;
    if (titlesResult.status === 'fulfilled') titles = titlesResult.value;
    if (rankResult.status === 'fulfilled') rank = rankResult.value;

    render();
  }

  // ----------------------------------------------------------------- actions

  /** Swap one livery slot. The other slot is never allowed to collide, so the picker disables it and this guards it too. */
  async function handlePickColor(slot: 'primary' | 'secondary', colorId: string): Promise<void> {
    const colony = colonies[0];
    if (!colony) return;
    const next = {
      primaryColor: slot === 'primary' ? colorId : colony.primaryColor,
      secondaryColor: slot === 'secondary' ? colorId : colony.secondaryColor,
    };
    if (next.primaryColor === next.secondaryColor) return;

    const previous = { primaryColor: colony.primaryColor, secondaryColor: colony.secondaryColor };
    // Optimistic: the swatch should react instantly to a tap.
    colonies = [{ ...colony, ...next }, ...colonies.slice(1)];
    render();
    try {
      await updateTeamColors(next);
      if (disposed) return;
      showToast(t('profile.toast.colorsUpdated'), 'success');
    } catch (err) {
      if (disposed) return;
      colonies = [{ ...colonies[0], ...previous }, ...colonies.slice(1)];
      render();
      showToast((err as Error).message, 'error');
    }
  }

  let pickerHandle: TitlePickerHandle | null = null;

  function openPicker(): void {
    if (titles.length === 0) {
      showToast(t('profile.toast.titlesLoading'), 'info');
      return;
    }
    pickerHandle?.close();
    pickerHandle = openTitlePicker(root, {
      playerName: profile?.username ?? profile?.email ?? '',
      playerEmail: profile?.email,
      enlisted: profile ? formatJoinDate(profile.createdAt) : undefined,
      power: profile ? formatPoints(profile.colonyPower) : undefined,
      titles,
      onEquipped: (titleId) => {
        if (disposed) return;
        const chosen = titles.find((x) => x.id === titleId) ?? null;
        titles = titles.map((x) => ({ ...x, equipped: x.id === titleId }));
        if (profile) {
          profile = {
            ...profile,
            titleId,
            titleLabel: chosen?.label ?? null,
            titleRarity: chosen?.rarity ?? null,
          };
        }
        render();
      },
      onBrowseShop: () => showToast(t('profile.toast.premiumTitles'), 'info'),
    });
  }

  function onClick(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    const actionEl = target.closest('[data-action]') as HTMLElement | null;
    if (!actionEl) return;
    const action = actionEl.dataset.action;
    if (action === 'pick-color') {
      void handlePickColor(actionEl.dataset.slot as 'primary' | 'secondary', actionEl.dataset.colorId as string);
    } else if (action === 'open-titles') {
      openPicker();
    } else if (action === 'open-rankings') {
      // The tab bar lives in the shell, outside this screen's own root — so
      // navigating to it means driving the real tab button rather than
      // reaching for a router this app doesn't have (see engine/tabs.ts).
      (document.querySelector('[data-tab-id="leaderboard"]') as HTMLButtonElement | null)?.click();
    }
  }
  container.addEventListener('click', onClick);

  mountAmbient();
  void load();
  // The countdown is the only thing that goes stale on its own (everything
  // else here only changes in response to a click), so this patches it in
  // place — it is not a polling fetch and never re-renders the screen.
  const seasonTick = setInterval(patchSeasonCountdown, SEASON_TICK_MS);

  return () => {
    disposed = true;
    // A title scene is a body-level overlay: it would otherwise outlive a tab switch.
    pickerHandle?.cancelScene();
    pickerHandle?.close();
    clearInterval(seasonTick);
    container.removeEventListener('click', onClick);
  };
}

export const profileTab: GameTab = {
  id: 'profile',
  label: t('nav.profile'),
  icon: 'profile',
  mount: renderProfileScreen,
};
